/**
 * Common Application Header Component
 * Uses React Router for navigation between pages
 */

import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Brain, Target, Wand2, User, ChevronDown, Building2, Settings, Users, FileText, BarChart3, LogOut, Sparkles, Check } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

type ActivePage = 'simulations' | 'candidates' | 'results' | 'comparison' | 'studio' | 'studio-plus';

interface AppHeaderProps {
    activePage?: ActivePage;
}

export function AppHeader({ activePage: activePageProp }: AppHeaderProps) {
    const { user, currentOrg, organizations, isAuthEnabled, signOut, switchOrganization, isOrgLoading } = useAuth();
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [showOrgMenu, setShowOrgMenu] = useState(false);
    const [isSwitchingOrg, setIsSwitchingOrg] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    const handleSignOut = async () => {
        setShowUserMenu(false);
        await signOut();
        navigate('/login');
    };

    const [showStudioMenu, setShowStudioMenu] = useState(false);
    const handleSwitchOrg = async (orgId: string) => {
        if (orgId === currentOrg?.id) {
            setShowOrgMenu(false);
            return;
        }
        setIsSwitchingOrg(true);
        try {
            await switchOrganization(orgId);
            setShowOrgMenu(false);
        } finally {
            setIsSwitchingOrg(false);
        }
    };

    const getActivePage = (): ActivePage => {
        if (activePageProp) return activePageProp;
        
        const path = location.pathname;
        if (path.includes('/studio-plus')) return 'studio-plus';
        if (path.includes('/studio')) return 'studio';
        if (path.includes('/comparison')) return 'comparison';
        if (path.includes('/results')) return 'results';
        if (path.includes('/candidates')) return 'candidates';
        return 'simulations';
    };

    const activePage = getActivePage();
    const isStudioActive = activePage === 'studio' || activePage === 'studio-plus';

    const handleNavigateToSimulations = () => navigate('/app');
    const handleNavigateToCandidates = () => navigate('/app/candidates');
    const handleNavigateToResults = () => navigate('/app/results');
    const handleNavigateToComparison = () => navigate('/app/comparison');
    const handleNavigateToStudio = () => {
        setShowStudioMenu(false);
        navigate('/app/studio');
    };
    const handleNavigateToStudioPlus = () => {
        setShowStudioMenu(false);
        navigate('/app/studio-plus');
    };

    return (
        <nav className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
            <div className="max-w-7xl mx-auto px-6">
                <div className="flex justify-between items-center h-16">
                    <div className="flex items-center space-x-8">
                        {/* Logo */}
                        <div 
                            className="flex items-center space-x-2 cursor-pointer"
                            onClick={handleNavigateToSimulations}
                        >
                            <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-lg flex items-center justify-center">
                                <Brain className="w-5 h-5 text-white" />
                            </div>
                            <span className="text-xl font-bold text-slate-900">Axone</span>
                        </div>

                        {/* Navigation Tabs */}
                        <div className="hidden md:flex items-center space-x-1">
                            {activePage === 'simulations' ? (
                                <div className="flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium bg-emerald-50 text-emerald-700">
                                    <Target className="w-4 h-4" />
                                    <span>Simulations</span>
                                </div>
                            ) : (
                                <button
                                    onClick={handleNavigateToSimulations}
                                    className="flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors"
                                >
                                    <Target className="w-4 h-4" />
                                    <span>Simulations</span>
                                </button>
                            )}

                            {activePage === 'candidates' ? (
                                <div className="flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium bg-emerald-50 text-emerald-700">
                                    <Users className="w-4 h-4" />
                                    <span>Candidates</span>
                                </div>
                            ) : (
                                <button
                                    onClick={handleNavigateToCandidates}
                                    className="flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors"
                                >
                                    <Users className="w-4 h-4" />
                                    <span>Candidates</span>
                                </button>
                            )}

                            {activePage === 'results' ? (
                                <div className="flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium bg-emerald-50 text-emerald-700">
                                    <FileText className="w-4 h-4" />
                                    <span>Results</span>
                                </div>
                            ) : (
                                <button
                                    onClick={handleNavigateToResults}
                                    className="flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors"
                                >
                                    <FileText className="w-4 h-4" />
                                    <span>Results</span>
                                </button>
                            )}

                            {activePage === 'comparison' ? (
                                <div className="flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium bg-emerald-50 text-emerald-700">
                                    <BarChart3 className="w-4 h-4" />
                                    <span>Comparison</span>
                                </div>
                            ) : (
                                <button
                                    onClick={handleNavigateToComparison}
                                    className="flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors"
                                >
                                    <BarChart3 className="w-4 h-4" />
                                    <span>Comparison</span>
                                </button>
                            )}

                            {/* Studio Dropdown */}
                            <div className="relative">
                                <button
                                    onClick={() => setShowStudioMenu(!showStudioMenu)}
                                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                                        isStudioActive
                                            ? 'bg-emerald-50 text-emerald-700'
                                            : 'text-slate-600 hover:bg-slate-100'
                                    }`}
                                >
                                    {activePage === 'studio-plus' ? (
                                        <Sparkles className="w-4 h-4" />
                                    ) : (
                                        <Wand2 className="w-4 h-4" />
                                    )}
                                    <span>{activePage === 'studio-plus' ? 'Studio+' : 'Studio'}</span>
                                    <ChevronDown className="w-3 h-3" />
                                </button>

                                {showStudioMenu && (
                                    <>
                                        <div 
                                            className="fixed inset-0 z-40" 
                                            onClick={() => setShowStudioMenu(false)}
                                        />
                                        <div className="absolute left-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-50">
                                            <button
                                                onClick={handleNavigateToStudio}
                                                className={`w-full text-left px-4 py-2.5 text-sm flex items-center space-x-3 ${
                                                    activePage === 'studio'
                                                        ? 'bg-emerald-50 text-emerald-700'
                                                        : 'text-slate-700 hover:bg-slate-50'
                                                }`}
                                            >
                                                <Wand2 className="w-4 h-4" />
                                                <div>
                                                    <div className="font-medium">Studio</div>
                                                    <div className="text-xs text-slate-500">Classic builder</div>
                                                </div>
                                            </button>
                                            <button
                                                onClick={handleNavigateToStudioPlus}
                                                className={`w-full text-left px-4 py-2.5 text-sm flex items-center space-x-3 ${
                                                    activePage === 'studio-plus'
                                                        ? 'bg-emerald-50 text-emerald-700'
                                                        : 'text-slate-700 hover:bg-slate-50'
                                                }`}
                                            >
                                                <Sparkles className="w-4 h-4" />
                                                <div>
                                                    <div className="font-medium">Studio+</div>
                                                    <div className="text-xs text-slate-500">AI-assisted builder</div>
                                                </div>
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center space-x-4">
                        {/* Organization Badge / Switcher */}
                        {isAuthEnabled && currentOrg && (
                            <div className="relative hidden md:block">
                                {organizations.length > 1 ? (
                                    <>
                                        <button
                                            onClick={() => setShowOrgMenu(!showOrgMenu)}
                                            disabled={isSwitchingOrg || isOrgLoading}
                                            className="flex items-center space-x-2 px-3 py-1.5 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors disabled:opacity-50"
                                        >
                                            {currentOrg.imageUrl ? (
                                                <img 
                                                    src={currentOrg.imageUrl} 
                                                    alt={currentOrg.name}
                                                    className="w-4 h-4 rounded object-cover"
                                                />
                                            ) : (
                                                <Building2 className="w-4 h-4 text-slate-500" />
                                            )}
                                            <span className="text-sm font-medium text-slate-700">{currentOrg.name}</span>
                                            <ChevronDown className={`w-3 h-3 text-slate-400 transition-transform ${showOrgMenu ? 'rotate-180' : ''}`} />
                                        </button>

                                        {showOrgMenu && (
                                            <>
                                                <div 
                                                    className="fixed inset-0 z-40" 
                                                    onClick={() => setShowOrgMenu(false)}
                                                />
                                                <div className="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-50">
                                                    <div className="px-3 py-2 border-b border-slate-100">
                                                        <span className="text-xs font-semibold text-slate-500 uppercase">Switch Organization</span>
                                                    </div>
                                                    {organizations.map((org) => (
                                                        <button
                                                            key={org.id}
                                                            onClick={() => handleSwitchOrg(org.id)}
                                                            disabled={isSwitchingOrg}
                                                            className={`w-full text-left px-3 py-2.5 hover:bg-slate-50 transition-colors flex items-center justify-between ${
                                                                org.id === currentOrg.id ? 'bg-emerald-50' : ''
                                                            }`}
                                                        >
                                                            <div className="flex items-center space-x-3">
                                                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                                                                    org.id === currentOrg.id ? 'bg-emerald-500' : 'bg-slate-200'
                                                                }`}>
                                                                    {org.imageUrl ? (
                                                                        <img 
                                                                            src={org.imageUrl} 
                                                                            alt={org.name}
                                                                            className="w-8 h-8 rounded-lg object-cover"
                                                                        />
                                                                    ) : (
                                                                        <Building2 className={`w-4 h-4 ${org.id === currentOrg.id ? 'text-white' : 'text-slate-500'}`} />
                                                                    )}
                                                                </div>
                                                                <div>
                                                                    <div className="text-sm font-medium text-slate-900">{org.name}</div>
                                                                    <div className="text-xs text-slate-500">
                                                                        {org.membersCount} member{org.membersCount !== 1 ? 's' : ''}
                                                                        {org.role && (
                                                                            <span className={`ml-2 px-1.5 py-0.5 rounded text-xs ${
                                                                                org.role === 'org:owner' ? 'bg-purple-100 text-purple-700' :
                                                                                org.role === 'org:admin' ? 'bg-blue-100 text-blue-700' :
                                                                                'bg-slate-100 text-slate-600'
                                                                            }`}>
                                                                                {org.role.replace('org:', '')}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            {org.id === currentOrg.id && (
                                                                <Check className="w-4 h-4 text-emerald-500" />
                                                            )}
                                                        </button>
                                                    ))}
                                                </div>
                                            </>
                                        )}
                                    </>
                                ) : (
                                    <div className="flex items-center space-x-2 px-3 py-1.5 bg-slate-100 rounded-lg">
                                        {currentOrg.imageUrl ? (
                                            <img 
                                                src={currentOrg.imageUrl} 
                                                alt={currentOrg.name}
                                                className="w-4 h-4 rounded object-cover"
                                            />
                                        ) : (
                                            <Building2 className="w-4 h-4 text-slate-500" />
                                        )}
                                        <span className="text-sm font-medium text-slate-700">{currentOrg.name}</span>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* User Menu */}
                        {isAuthEnabled && user && (
                            <div className="relative">
                                <button
                                    onClick={() => setShowUserMenu(!showUserMenu)}
                                    className="flex items-center space-x-3 px-3 py-2 rounded-lg hover:bg-slate-100 transition-colors duration-200"
                                >
                                    <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center">
                                        {user.imageUrl ? (
                                            <img src={user.imageUrl} alt={user.fullName || 'User'} className="w-8 h-8 rounded-full" />
                                        ) : (
                                            <User className="w-4 h-4 text-white" />
                                        )}
                                    </div>
                                    <div className="hidden md:block text-left">
                                        <div className="text-sm font-medium text-slate-900">{user.fullName || user.email || 'User'}</div>
                                        <div className="text-xs text-slate-500">{user.email}</div>
                                    </div>
                                    <ChevronDown className="w-4 h-4 text-slate-400" />
                                </button>

                                {showUserMenu && (
                                    <>
                                        <div 
                                            className="fixed inset-0 z-40" 
                                            onClick={() => setShowUserMenu(false)}
                                        />
                                        <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-50">
                                            <div className="px-4 py-2 border-b border-slate-100">
                                                <div className="text-sm font-medium text-slate-900">{user.fullName || user.email}</div>
                                                <div className="text-xs text-slate-500">{user.email}</div>
                                            </div>
                                            {currentOrg && (
                                                <div className="px-4 py-2 border-b border-slate-100">
                                                    <div className="flex items-center space-x-2 text-sm text-slate-600">
                                                        {currentOrg.imageUrl ? (
                                                            <img 
                                                                src={currentOrg.imageUrl} 
                                                                alt={currentOrg.name}
                                                                className="w-4 h-4 rounded object-cover"
                                                            />
                                                        ) : (
                                                            <Building2 className="w-4 h-4" />
                                                        )}
                                                        <span>{currentOrg.name}</span>
                                                    </div>
                                                    {currentOrg.membersCount !== undefined && (
                                                        <div className="text-xs text-slate-500 mt-1 ml-6">
                                                            {currentOrg.membersCount}/{currentOrg.maxMembers} members
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                            <button
                                                onClick={() => {
                                                    setShowUserMenu(false);
                                                    navigate('/app/settings');
                                                }}
                                                className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center space-x-2"
                                            >
                                                <Settings className="w-4 h-4" />
                                                <span>Settings</span>
                                            </button>
                                            <div className="border-t border-slate-100 my-1" />
                                            <button
                                                onClick={handleSignOut}
                                                className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2"
                                            >
                                                <LogOut className="w-4 h-4" />
                                                <span>Sign Out</span>
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </nav>
    );
}
